import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../@types/redux';
import { dataSetCountries, dataSetRegions } from '../../../store/masterData/Actions';
import { formatDateTime, upsertArrayElement } from '../../../services/appFunctionsService';
import CountryHttpService from '../../../services/http/masterData/countryHttpService';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../store/general/Functions';
import RegionHttpService from '../../../services/http/masterData/regionHttpService';
import Screen from '../../../components/Screen';
import CountryForm from './form/CountryForm';
import { ConfirmationDialog, CustomTable, ProjectDialog } from '@zz2/zz2-ui';
import { IApiError } from '../../../@types/other';
import { setCountryMasterDataIndexedDB, syncMasterData } from '../../../services/masterDataSyncService';
import CountryModelHelper from '../../../@types/model/masterData/country/countryModelHelper';
import { useLocation } from 'react-router-dom';
import { RightGrantsEnum } from '../../../@types/enum/rightGrantsEnum';
import { ICountry } from '../../../@types/model/masterData/country/country';
import { ICountryFormValues } from '../../../@types/model/masterData/country/countryFormValues';
import UserHelper from '../../../services/helper/userHelper';

const CountryScreen = () : React.ReactElement => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const currentUser = useAppSelector(x => x.auth.session?.user ?? null);
    const countries = useAppSelector(x => x.masterData.countries);
    const regions = useAppSelector(x => x.masterData.regions);
    const selectedOrganizationIds = useAppSelector(x => x.data.selectedOrganizationIds);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedCountry, setSelectedCountry] = useState<ICountry | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const [deletingCountry, setDeletingCountry] = useState<ICountry | null>(null);

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        fetchData();
    }, []);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    const fetchData = async () => {
        const isIndexedDBAvailable = !!self.indexedDB;
        if (isIndexedDBAvailable) {
            await syncMasterData(false);
        } else {
            setIsLoading(true);
            try {
                const res = await CountryHttpService.getCountryData();
                const res2 = await RegionHttpService.getRegionData();
                dispatch(dataSetCountries(res.data));
                dispatch(dataSetRegions(res2.data));
            } catch (error : unknown) {
                const apiError = error as IApiError | undefined;
                generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading countries.');
            } finally {
                setIsLoading(false);
            }
        }
    };

    const onSubmit = async (values : ICountryFormValues) => {
        setIsLoading(true);

        try {
            const upsert = CountryModelHelper.createUpsert(values);
            const res = await CountryHttpService.addOrUpdateCountry(upsert);
            const updatedList = upsertArrayElement(countries, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetCountries(updatedList));
            await setCountryMasterDataIndexedDB(updatedList);

            generalShowSuccessSnackbar(selectedCountry ? 'Country updated successfully.' : 'Country added successfully.');
            onModalDismiss();
        } catch (error : unknown) {
            const apiError = error as IApiError | undefined;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating country data.');
        } finally {
            setIsLoading(false);
        }
    };

    const removeCountry = async () => {
        if (!deletingCountry) return;
        const updatedCountry = { ...deletingCountry, isActive: false };
        setIsLoading(true);
        try {
            const res = await CountryHttpService.deleteCountry(updatedCountry.id);
            const updatedList = upsertArrayElement(countries, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetCountries(updatedList));
            await setCountryMasterDataIndexedDB(updatedList);
            generalShowSuccessSnackbar('Country deleted successfully.');
        } catch (error : unknown) {
            const apiError = error as IApiError | undefined;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting country.');
        } finally {
            closeDeletePopup();
            setIsLoading(false);
        }
    };

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const openModal = () => {
        setIsDialogOpen(true);
    };

    const onModalDismiss = () => {
        setIsDialogOpen(false);
        setSelectedCountry(null);
    };

    const edit = (selectedRow : ICountry) => {
        setSelectedCountry(selectedRow);
        openModal();
    };

    const onDelete = (selectedRow : ICountry) : void => {
        setDeletingCountry(selectedRow);
    };

    const closeDeletePopup = () : void => {
        setDeletingCountry(null);
    };

    const deleteButtonTooltip = (row : ICountry) : string => {
        let message = 'Delete';

        if (!userHasRights.deleteRight) {
            message = UserHelper.getRelatedRightToolTipMessage('Country', RightGrantsEnum.Delete);
        } else if (!row.isActive) {
            message = 'Already Inactive.';
        }

        return message;
    };

    const getRegionName = useCallback((regionId : number) => {
        const region = regions.find(x => x.id === regionId);
        return region?.name ?? '';
    }, [regions]);

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const userHasRights = useMemo(() => {
        return UserHelper.userRelatedRights(location.pathname, currentUser?.rights);
    }, [currentUser, location]);

    const initialValues = useMemo(() => {
        if (isDialogOpen) {
            return CountryModelHelper.createFormValues(selectedCountry, regions);
        }
    }, [selectedCountry, isDialogOpen, regions]);

    const filteredRows = useMemo(() => {
        return countries.filter((x) => {
            const region = regions.find(y => y.id === x.regionId && y.isActive);
            return region?.organizationIds.some(y => selectedOrganizationIds?.some(z => z === y));
        });
    }, [countries, regions, selectedOrganizationIds]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <Screen isLoading={isLoading} isScrollable={false}>
            <div className='fdc hfill'>
                <Card className='fdc hfill'>
                    <CustomTable<ICountry>
                    
                        enableAdding
                        // addTooltipRightValidation={!userHasRights.addRight}
                        disableAddButton={!userHasRights.addRight || isLoading}
                        addTooltip={!userHasRights.addRight ? UserHelper.getRelatedRightToolTipMessage('Country', RightGrantsEnum.Add) : 'Add'}
                        addFunction={openModal}
                        // addTooltipOnClick={() : void => requestAccess('Add')}
                    
                        enableEditing
                        // editTooltipRightValidation={!userHasRights.editRight}
                        disableEditButton={!userHasRights.editRight || isLoading}
                        editTooltip={!userHasRights.editRight ? UserHelper.getRelatedRightToolTipMessage('Country', RightGrantsEnum.Edit) : 'Edit'}
                        editFunction={edit}
                        // editTooltipOnClick={() : void => requestAccess('Edit')}

                        enableDeleting
                        // deleteTooltipRightValidation={!userHasRights.deleteRight}
                        disableDeleteButton={(row : ICountry) : boolean => !userHasRights.deleteRight || !row.isActive || isLoading}
                        deleteTooltip={deleteButtonTooltip}
                        deleteFunction={onDelete}
                        // deleteTooltipOnClick={() : void => requestAccess('Delete')}

                        enableSorting
                        enableFiltering
                        enablePagination
                        fitWidthToPage
                        columns={[
                            { title: 'Code', field: 'code', enableFiltering: true, enableSorting: true },
                            { title: 'Name', field: 'name', enableFiltering: true, enableSorting: true },
                            { title: 'Region', field: 'regionId', formatFunction: getRegionName, enableFiltering: true, enableSorting: true },
                            { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                            { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                            { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                            { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                        ]}
                        rows={filteredRows}
                        initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                        pageSizes={[50, 150, 250, 500, 1000]}
                        pageHeight={190}
                        isActive={row => row.isActive}
                    />
                </Card>
            </div>
            {isDialogOpen && initialValues &&
                <ProjectDialog
                    title={selectedCountry ? 'Edit Country' : 'Add Country'}
                    isLoadingCircular={isLoading}
                    isOpen={isDialogOpen}
                    onClose={onModalDismiss}
                    maxWidth={'sm'}
                    fullWidth
                >
                    <CountryForm
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={onModalDismiss}
                        isLoading={isLoading}
                    />
                </ProjectDialog>
            }
            <ConfirmationDialog
                title={'Inactivate Country'}
                open={!!deletingCountry}
                description={'Are you sure you would like to set the following item to inactive?'}
                onAccept={removeCountry}
                onClose={closeDeletePopup}
                dialogType={'red'}
                isLoading={isLoading}
            />
        </Screen>
    );
};

export default CountryScreen;
